import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

import Header from "./components/Header";
import Recherche from "./pages/common/expert/Recherche";
import Loader from "./components/Loader";
import Error from "./components/Error";

import Login from "./pages/common/Login";
import Logout from "./pages/common/Logout";
import Signup from "./pages/common/Signup";
import Home from "./pages/common/expert/Home";
import GedFileExtract from "./pages/common/expert/GedFileExtract.jsx";
import Expertise from "./pages/common/expert/Expertise";
import ManagerExpertiseCommon from "./pages/common/normal/ManagerExpertiseCommon";
import ManagerExpertise from "./pages/common/expert/ManagerExpertise";
import ManagerOrganization from "./pages/common/expert/ManagerOrganization";
import ActivateAccount from "./pages/common/ActivateAccount";
import PasswordReset from "./components/PasswordReset";
import NouveauMotDePasse from "./components/PasswordReset/Nouveau";
import RoleRedirect from "./components/RoleRedirect";
import ParcoursAssure, { AccueilAssure } from "./parcours/ParcoursAssure";
import PageDepot from "./parcours/PageDepot";
import PageTransfert from "./parcours/PageTransfert";
import PageLiaison from "./parcours/PageLiaison";
import PageInvitation from "./parcours/PageInvitation";
import Profil from "./parcours/Profil";
import Aide from "./parcours/Aide";
import PiedDePage from "./parcours/PiedDePage";
import { Conditions, Confidentialite } from "./parcours/Legal";

import axiosInstance from "./axiosInstance";
import { AuthProvider } from "./context/AuthContext";
import { ProtectedRoute, RoleRoute } from "./routes/ProtectedRoutes";

import "./index.css";

const App = () => {
  const [pendingRequests, setPendingRequests] = useState(0);

  useEffect(() => {
    // Une requête `silencieux: true` (un contrôle en tâche de fond) ne pose
    // pas le voile « Chargement… » : toutes les 5 secondes, il faisait
    // clignoter l'écran entier.
    const compte = (config) => !config?.silencieux;

    const requestInterceptor = axiosInstance.interceptors.request.use(
      config => {
        if (compte(config)) setPendingRequests(prev => prev + 1);
        return config;
      },
      error => {
        if (compte(error?.config)) setPendingRequests(prev => Math.max(prev - 1, 0));
        return Promise.reject(error);
      }
    );
  
    const responseInterceptor = axiosInstance.interceptors.response.use(
      response => {
        if (compte(response.config)) setPendingRequests(prev => Math.max(prev - 1, 0));
        return response;
      },
      error => {
        if (compte(error?.config)) setPendingRequests(prev => Math.max(prev - 1, 0));
        return Promise.reject(error);
      }
    );
  
    return () => {
      axiosInstance.interceptors.request.eject(requestInterceptor);
      axiosInstance.interceptors.response.eject(responseInterceptor);
    };
  }, []);
  
  

  return (
    <React.StrictMode>
      {pendingRequests > 0 && <Loader />}

      <AuthProvider>
        <Router>
          <Header />

          <Row>
            <Col>
              <Routes>
                {/* Public */}
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/activate/:uidb64/:token/" element={<ActivateAccount />} />
                <Route path="/logout" element={<Logout />} />
                {/* Mot de passe perdu : demander le lien, puis en choisir
                    un nouveau depuis ce lien. */}
                <Route path="/mot-de-passe-oublie" element={<PasswordReset />} />
                <Route path="/mot-de-passe/:uidb64/:token"
                       element={<NouveauMotDePasse />} />
                <Route path="/ged/file/:fileId" element={<GedFileExtract />} />
                {/* Dépôt depuis un téléphone : le porteur du jeton n'a pas de compte. */}
                <Route path="/depot/:jeton" element={<PageDepot />} />
                {/* Lien de téléchargement reçu par courriel : sans compte. */}
                <Route path="/telechargement/:jeton" element={<PageTransfert />} />
                <Route path="/relier/:jeton" element={<PageLiaison />} />
                <Route path="/invitation/:jeton" element={<PageInvitation />} />
                <Route
                  path="/aide"
                  element={<ProtectedRoute><Aide /></ProtectedRoute>}
                />
                <Route
                  path="/profil"
                  element={<ProtectedRoute><Profil /></ProtectedRoute>}
                />
                {/* Mentions légales : atteignables sans compte. */}
                <Route path="/conditions" element={<Conditions />} />
                <Route path="/confidentialite" element={<Confidentialite />} />
                <Route path="/" element={<RoleRedirect />} />
                {/* Parcours assuré — arbre distinct du front expert */}
                <Route
                  path="/mon-dossier"
                  element={
                    <RoleRoute allowedOrgRoles={["NORMAL", "INTERMEDIAIRE"]}>
                      <AccueilAssure />
                    </RoleRoute>
                  }
                />
                <Route
                  path="/mon-dossier/:expertiseId/:etape"
                  element={
                    <RoleRoute allowedOrgRoles={["NORMAL", "INTERMEDIAIRE"]}>
                      <ParcoursAssure />
                    </RoleRoute>
                  }
                />
                {/* Normal user — /home et /list_expertise retirés : le
                    parcours assuré les remplace. `Home` sert encore à /tdb. */}
                  <Route
                    path="/gestion_expertise/:expertiseId"
                    element={
                      <RoleRoute allowedOrgRoles={["NORMAL"]}>
                        <ManagerExpertiseCommon/>
                      </RoleRoute>
                    }
                  />
                {/* Expert */}
                <Route
                    path="/tdb"
                    element={
                      <RoleRoute allowedOrgRoles={["EXPERT"]}>
                        <Home />
                      </RoleRoute>
                    }
                  />
                <Route
                  path="/expertise"
                  element={
                    <RoleRoute allowedOrgRoles={["EXPERT"]}>
                      <Expertise />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/edit-expertise/:expertiseId"
                  element={
                    <RoleRoute allowedOrgRoles={["EXPERT"]}>
                      <ManagerExpertise />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/recherche"
                  element={
                    <RoleRoute allowedOrgRoles={["EXPERT"]}>
                      <Recherche />
                    </RoleRoute>
                  }
                />

                <Route
                  path="/manager"
                  element={
                    <RoleRoute allowedOrgRoles={["EXPERT"]}>
                      <ManagerOrganization />
                    </RoleRoute>
                  }
                />

                <Route path="*" element={<Error />} />
              </Routes>
              <PiedDePage />
            </Col>
          </Row>
        </Router>
      </AuthProvider>
    </React.StrictMode>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<App />);
